import { useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';
import { useToken } from '@/common/helper';
import { useExitPreview } from '@/hooks/useExitPreview/useExitPreview';

// Client-side mirror of the backend's PermissionPreviewSession 30-minute
// inactivity window (accounts/middleware.py `_resolve_preview_session`).
// Firing the real Exit Preview API proactively -- while the session is
// still valid -- means this is always a normal, successful exit rather
// than a recovery from a session the backend has already invalidated.
const PREVIEW_IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
] as const;

/**
 * Automatically exits an active preview session after 30 minutes of no
 * mouse/keyboard/touch/scroll activity, returning the admin to their own
 * identity and to the settings screen the preview was launched from.
 * A no-op whenever no preview session is active.
 */
export const usePreviewIdleTimeout = (): void => {
  const { getPreviewState } = useToken();
  const { isPreview } = getPreviewState();
  const { exitPreview } = useExitPreview();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isExitingRef = useRef(false);

  const handleTimeout = useCallback(async () => {
    if (isExitingRef.current) return;
    isExitingRef.current = true;
    try {
      const didExit = await exitPreview({ silent: true });
      if (didExit) {
        message.info('Preview session ended after 30 minutes of inactivity.');
      }
    } finally {
      isExitingRef.current = false;
    }
  }, [exitPreview]);

  useEffect(() => {
    if (typeof window === 'undefined' || !isPreview) return undefined;

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(handleTimeout, PREVIEW_IDLE_TIMEOUT_MS);
    };

    resetTimer();
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, resetTimer, { passive: true })
    );

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, resetTimer)
      );
    };
  }, [isPreview, handleTimeout]);
};

export default usePreviewIdleTimeout;
